import React, { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, apiRequest } from '../../msalconfig';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomButton from '@ui-components/Button/CustomButton';
import './signin.css';
import { useGetOpsUserDetails } from '../../API/hooks/loginHook';
import QLerLogo from '../../assets/images/qler_logo.svg';
import { CustomCard } from '@qler-view/shared_lib/src/index.js';
import { hasRole, setUserToStorage } from './storageUtility';

export default function AuthenticationHandler({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [userDetailsReady, setUserDetailsReady] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const userEmail = accounts[0]?.username;
  const { operationUserDetails, isError, refetch } =
    useGetOpsUserDetails(userEmail);
  console.log('operationUserDetails', operationUserDetails);
  const [redirected, setRedirected] = useState(false);

  const isAccessDenied = location.pathname === '/access-denied';

  // HANDLE REDIRECT RESPONSE
  useEffect(() => {
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  useEffect(() => {
    const acquireToken = async () => {
      if (!isAuthenticated || inProgress !== 'none') return;

      const account = instance.getActiveAccount();
      if (!account) return;

      try {
        const response = await instance.acquireTokenSilent({
          ...apiRequest,
          account,
        });

        setToken(response.accessToken);
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(apiRequest);
        } else {
          console.error('Token acquisition failed:', err);
        }
      }
    };

    acquireToken();
  }, [isAuthenticated, inProgress, instance]);

  //ROLE VALIDATION
  useEffect(() => {
    const account = instance.getActiveAccount();
    if (!account) return;

    const roles = (account.idTokenClaims?.roles as string[]) || [];

    const allowed = roles.includes('Ops-Staff') || roles.includes('Ops-Admin');

    if (!allowed && !isAccessDenied) {
      navigate('/access-denied', { replace: true });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    console.log('Authentication check running');
    console.log({ token, operationUserDetails, isError, redirected });

    if (token && !operationUserDetails && !isError) {
      console.log('Refetching user details...');
      refetch();
    }
  }, [token, operationUserDetails, isError, refetch]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (operationUserDetails && !redirected) {
      console.log('User details ready, redirecting...');
      if (operationUserDetails.userId) {
        localStorage.setItem('userId', operationUserDetails.userId.toString());
      }

      setUserToStorage({
        userId: operationUserDetails.userId,
        firstName: operationUserDetails.firstName,
        lastName: operationUserDetails.lastName,
        email: operationUserDetails.email,
        phone: operationUserDetails.phone,
        username: operationUserDetails.username,
        role: operationUserDetails.role,
        status: operationUserDetails.status,
      });

      localStorage.setItem(
        'username',
        operationUserDetails.username
          ? operationUserDetails.username.toString()
          : ''
      );
      setUserDetailsReady(true);
      setRedirected(true);
    }

    if (location.pathname === '/' || location.pathname === '/login') {
      if (hasRole(['ops-admin'])) {
        navigate('/ops/partners', { replace: true });
        history.replaceState(null, '', '/ops/partners');
      } else {
        navigate('/ops/consults', { replace: true });
        history.replaceState(null, '', '/ops/consults');
      }
    }
  }, [operationUserDetails, redirected, navigate, location]);

  if (isError) {
    return (
      <Box className="loading-entra">
        <CustomTypography id="loading-text" variant="h4">
          Error loading user details
        </CustomTypography>
      </Box>
    );
  }

  if (inProgress !== 'none') {
    return (
      <Box className="loading-entra">
        <CustomTypography id="loading-text" variant="h4">
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  if (!isAuthenticated && !isAccessDenied) {
    if (location.pathname !== '/') {
      history.replaceState(null, '', '/');
    }
    return (
      <Box className="container-landing-page">
        <Box className="center-div">
          <CustomCard id="home-login-card" className="p-32">
            <Box className="logo-qler">
              <QLerLogo />
            </Box>
            <Box>
              <CustomButton
                id="login-operation-portal-button"
                variant="contained"
                color="primary"
                onClick={() => instance.loginRedirect(loginRequest)}
                className="login-microsoft-button"
              >
                Login with SSO
              </CustomButton>
            </Box>
          </CustomCard>
        </Box>
      </Box>
    );
  }

  if (!userDetailsReady) {
    return (
      <Box className="loading-entra">
        <CustomTypography id="loading-text" variant="h4">
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  return <>{children}</>;
}
